/**
 * The one thing this application stores on the device.
 *
 * Everything else about a session lives in an HttpOnly cookie the browser
 * manages and JavaScript cannot read - that is the property that makes an XSS
 * here unable to steal a credential, and it has not changed. What is stored
 * here is an *email address*, at the product owner's request, so that someone
 * returning to the sign-in screen finds the field already filled and only has
 * to type a password.
 *
 * What is deliberately **not** stored, and will not be:
 *
 * * the password - a browser's password manager keeps those encrypted and
 *   behind the operating system's own unlock; a copy in `localStorage` would
 *   be plain text readable by any script that ever runs on this origin,
 * * the session token - there is none in JavaScript to store,
 * * anything about who the person is beyond the address they typed.
 *
 * Every call is wrapped: Safari in private mode throws on `localStorage`
 * access, and a sign-in screen that crashes because it could not remember a
 * convenience is a far worse failure than forgetting one.
 */

const KEY = "sentycap.signin.email";

export function readRememberedEmail(): string | null {
  try {
    const value = window.localStorage.getItem(KEY);
    return value && value.length <= 254 ? value : null;
  } catch {
    return null;
  }
}

export function rememberEmail(email: string): void {
  try {
    window.localStorage.setItem(KEY, email);
  } catch {
    /* Storage denied or full. The next sign-in simply starts empty. */
  }
}

export function forgetEmail(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* Nothing to recover: there is no state here worth reporting a failure for. */
  }
}
